import { twMerge } from 'tailwind-merge';
import ListItem from '@mui/material/ListItem';
import { useDateFormatPreference, useProject } from '@witmetrics/data-store';
import { isStatusApplicable } from '@witmetrics/unison-core';
import { isEmpty } from '@witmetrics/utils/misc';
import { formatTextDate } from '@witmetrics/utils/time';
import { useAppState } from '@/providers/AppStateProvider';
import Card from '@/components/Card';
import CaseTypeIcon from '@/components/CaseTypeIcon';
import IconButton from '@/components/IconButton';
import ProjectStatusChip from '@/components/ProjectStatusChip';
import TruncatedText from '@/components/TruncatedText';
import { CloseIcon } from '@/icons';
import { classes, getTitle } from './utils';

export type ProjectsListItemProps = {
  active?: boolean;
  flat?: boolean;
  showUser?: boolean;
  className?: string;
  projectId: number;
  variant?: 'inline' | 'card';
  onSelect?: () => void;
};

export default function ProjectsListItem({
  active,
  flat,
  showUser,
  className,
  projectId,
  variant = 'inline',
  onSelect,
}: ProjectsListItemProps) {
  const { onSelectProject } = useAppState();
  const project = useProject(projectId);
  const dateFormat = useDateFormatPreference();

  if (!project) return null;

  const handleClick = onSelect ?? (() => onSelectProject(projectId));

  const title = getTitle(project, showUser);
  const typeLabel = getTitle(project);

  const hasName = !isEmpty(project.name) && project.name !== title;

  return (
    <ListItem disablePadding onClick={active ? undefined : handleClick}>
      <Card
        className={twMerge(
          variant === 'card' ? classes.cardRow : classes.row,
          active && classes.activeRow,
          flat && classes.flatRow,
          className
        )}>
        <CaseTypeIcon
          className={classes.avatar}
          projectTypeId={project.unisonProjectTypeId}
          iconClassName={classes.avatarIcon}
        />
        <div className={classes.rowContent}>
          <TruncatedText className={classes.rowTitle}>{title}</TruncatedText>
          {(showUser || hasName) && (
            <div className={classes.rowSecondLine}>
              {showUser && <span className={classes.type}>{typeLabel}</span>}
              {hasName && <span className={classes.code}>{project.name}</span>}
            </div>
          )}
        </div>
        {isStatusApplicable(project.unisonProjectTypeId) && (
          <ProjectStatusChip
            className={classes.rowChip}
            projectStatusId={project.unisonProjectStatusId}
          />
        )}
        <div className={classes.rowDate}>
          {formatTextDate(project.updatedAt, dateFormat, true)}
        </div>
        {active && onSelect && (
          <IconButton className={classes.closeButton} onClick={onSelect}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        )}
      </Card>
    </ListItem>
  );
}
